import { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import i18n, { setLanguageFromPath } from '@/config/i18n';
import type { SupportedLanguage } from '@/lib/languageUtils';
import { LANG_PREFIXES } from './LangWrapper';

const LANG_SET = new Set<string>(LANG_PREFIXES);

/**
 * Protected/app routes (dashboard, cctv-design, auth, …) are unprefixed by design —
 * language lives in useLanguage(), not the URL. If a user lands on /id/dashboard,
 * set the language and redirect to the canonical /dashboard so the hook/i18n take over.
 */
export default function LangRedirect({ to }: { to: string }) {
  // The routes are spelled out per language (`pl/dashboard`), so there is no
  // :lang param to read; the first segment of the path is the language.
  const params = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const lang = params.lang ?? pathname.split('/')[1];
  const { changeLanguage } = useLanguage();

  // Set before the page renders, once: the switch announces itself to every
  // translated component, whose re-render rendered this again and switched
  // again, until React gave up (error #185 on /settings, 18.10).
  if (lang && LANG_SET.has(lang) && i18n.language !== lang) {
    setLanguageFromPath(`/${lang}`);
  }

  useEffect(() => {
    if (lang && LANG_SET.has(lang)) {
      changeLanguage(lang as SupportedLanguage);
    }
  }, [lang, changeLanguage]);

  // The query and hash go along: /pl/cctv-design?project=… opened a new,
  // empty project, and /pl/auth?redirect=… lost where to return after sign-in.
  const target = typeof window !== 'undefined' ? to + window.location.search + window.location.hash : to;
  return <Navigate to={target} replace />;
}
